import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  IconButton,
  TextField,
  Autocomplete,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';

const ShiftBrowser = ({
  guards,
  browseGuardId,
  browseGuardName,
  browseWeekStart,
  browseWeekEnd,
  filteredShifts,
  onBrowseGuardChange,
  onPreviousWeek,
  onNextWeek,
  onShiftClick,
  onAddNewShift,
}) => {
  const formatDuration = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours.toFixed(1)} hrs`;
  };

  const formatBrowseWeekRange = useMemo(() => {
    const start = browseWeekStart.format('MMM D');
    const end = browseWeekEnd.format('MMM D, YYYY');
    return `${start} - ${end}`;
  }, [browseWeekStart, browseWeekEnd]);

  const formatShiftListItem = (shiftItem) => {
    const startMoment = moment(shiftItem.StartTime);
    const endMoment = moment(shiftItem.EndTime);
    const dayLabel = startMoment.format('ddd, MMM D');
    const timeLabel = `${startMoment.format('h:mm A')} - ${endMoment.format('h:mm A')}`;
    return { dayLabel, timeLabel };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Title */}
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
          Shift View
        </Typography>
        {browseGuardId ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Viewing shifts for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {browseGuardName}
            </Box>
            {' '}on the{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              week of {browseWeekStart.format('MMM D')} to {browseWeekEnd.format('MMM D, YYYY')}
            </Box>
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Select a guard to browse their shifts, or click a patrol on the calendar
          </Typography>
        )}
      </Box>

      {/* Guard Selector */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, marginBottom: 2 }}>
        <PersonIcon sx={{ color: 'action.active', mt: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={guards.find((g) => g.id === browseGuardId) || null}
            onChange={(_, newValue) => {
              onBrowseGuardChange?.(newValue?.id || null, newValue?.name || '');
            }}
            options={guards}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Guard"
                placeholder="Search guards..."
                size="small"
              />
            )}
            fullWidth
            size="small"
          />
        </Box>
      </Box>

      {browseGuardId ? (
        <>
          {/* Week Navigation */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 2,
              backgroundColor: 'action.hover',
              borderRadius: 1,
              padding: 1,
            }}
          >
            <IconButton size="small" onClick={onPreviousWeek}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatBrowseWeekRange}
            </Typography>
            <IconButton size="small" onClick={onNextWeek}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Divider sx={{ marginBottom: 2 }} />

          {/* Shifts List */}
          {filteredShifts.length > 0 ? (
            <Stack
              spacing={1.5}
              sx={{
                flex: 1,
                overflow: 'hidden',
                paddingBottom: 1,
              }}
            >
              {filteredShifts.map((shiftItem) => {
                const { dayLabel, timeLabel } = formatShiftListItem(shiftItem);
                return (
                  <Card
                    key={shiftItem.id}
                    elevation={1}
                    onClick={() => onShiftClick(shiftItem)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                      flexShrink: 0,
                      '&:hover': {
                        elevation: 3,
                        backgroundColor: 'action.hover',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {dayLabel}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {timeLabel}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip
                            label={`${shiftItem.patrolCount} patrol${shiftItem.patrolCount > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={formatDuration(shiftItem.totalDuration)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Add Shift Button */}
              <Card
                elevation={0}
                onClick={onAddNewShift}
                sx={{
                  cursor: 'pointer',
                  border: '1px dashed',
                  borderColor: 'divider',
                  backgroundColor: 'transparent',
                  flexShrink: 0,
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      Add Shift
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography
                  variant="body2"
                  sx={{ color: 'text.disabled', textAlign: 'center' }}
                >
                  No shifts scheduled this week
                </Typography>
              </Box>

              {/* Add Shift Button */}
              <Card
                elevation={0}
                onClick={onAddNewShift}
                sx={{
                  cursor: 'pointer',
                  border: '1px dashed',
                  borderColor: 'divider',
                  backgroundColor: 'transparent',
                  width: '100%',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      Add Shift
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      ) : (
        /* No guard selected - show helper icon */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            opacity: 0.5,
          }}
        >
          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        </Box>
      )}
    </Box>
  );
};

ShiftBrowser.propTypes = {
  guards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  browseGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  browseGuardName: PropTypes.string,
  browseWeekStart: PropTypes.object.isRequired,
  browseWeekEnd: PropTypes.object.isRequired,
  filteredShifts: PropTypes.array.isRequired,
  onBrowseGuardChange: PropTypes.func,
  onPreviousWeek: PropTypes.func,
  onNextWeek: PropTypes.func,
  onShiftClick: PropTypes.func,
  onAddNewShift: PropTypes.func,
};

export default memo(ShiftBrowser);
